/**
 * Converts an HTML string (e.g. quill editor output) to plain text.
 * Block-level closers and <br> become newlines so the result approximates what is
 * actually delivered via SMS, and quill's empty state ('<p><br></p>') becomes ''.
 */
export function stripHtmlToText(html: string): string {
	if (!html) return ''
	const withBreaks = html
		// Quill marks empty lines with a trailing <br> inside the block — drop it so
		// the block closer alone produces the newline ('<p><br></p>' → '')
		.replace(/<br[^>]*>\s*(<\/(?:p|div|li|h[1-6])>)/gi, '$1')
		.replace(/<br[^>]*>/gi, '\n')
		.replace(/<\/(?:p|div|li|h[1-6])\s*>/gi, '\n')
	// DOMParser works on a detached document — markup is never assigned to a live node
	const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
	const text = doc.body.textContent ?? ''
	return text.replace(/\u00a0/g, ' ').trim()
}

export const GSM_SINGLE_SEGMENT_LIMIT = 160
export const GSM_CONCAT_SEGMENT_LIMIT = 153
export const UCS2_SINGLE_SEGMENT_LIMIT = 70
export const UCS2_CONCAT_SEGMENT_LIMIT = 67

// Approximation of the GSM-7 basic character set: printable ASCII (space..tilde) plus newlines.
// Anything outside it (emoji, accented letters, smart quotes) forces UCS-2 encoding.
const NON_GSM_CHAR = /[^ -~\n\r]/

export interface SmsSegmentInfo {
	/** Number of segments the message splits into (0 for empty text) */
	segments: number
	/** Max characters per segment for the detected encoding and segment count */
	segmentLimit: number
	/** True when a non-GSM character forces UCS-2 encoding */
	isUnicode: boolean
}

/**
 * Approximate SMS segment calculation for a plain-text message.
 * GSM-7 messages fit 160 chars in a single segment and 153 per segment when concatenated;
 * any non-GSM character (e.g. emoji) switches to UCS-2 with 70 / 67 limits.
 * Approximate because GSM-7 extension characters (€, [, ], etc.) counting as 2 septets is ignored.
 */
export function smsSegmentInfo(text: string): SmsSegmentInfo {
	const isUnicode = NON_GSM_CHAR.test(text)
	const singleLimit = isUnicode ? UCS2_SINGLE_SEGMENT_LIMIT : GSM_SINGLE_SEGMENT_LIMIT
	const concatLimit = isUnicode ? UCS2_CONCAT_SEGMENT_LIMIT : GSM_CONCAT_SEGMENT_LIMIT
	if (!text) return { segments: 0, segmentLimit: singleLimit, isUnicode }
	if (text.length <= singleLimit) return { segments: 1, segmentLimit: singleLimit, isUnicode }
	return { segments: Math.ceil(text.length / concatLimit), segmentLimit: concatLimit, isUnicode }
}
